// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate, useSearch } from "@tanstack/react-router";
import type { PaginationState, SortingState } from "@tanstack/react-table";
import { Filter, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import { type DeviceEntry, type DevicesListResult, type DevicesSortBy, useDevicesQuery } from "@/api/hooks/use-devices";
import { useStoresQuery } from "@/api/hooks/use-stores";
import { DataTable, type DataTableColumnDef } from "@/components/data-table";
import { PageHeader } from "@/components/layout/page-header";
import { ChainLink } from "@/components/shared/chain-link";
import { DateTimeValue, DateValue } from "@/components/shared/datetime-value";
import { FilterPresets } from "@/components/shared/filter-presets";
import { FilterSelect } from "@/components/shared/filter-select";
import { FilterToggle } from "@/components/shared/filter-toggle";
import { RowId } from "@/components/shared/row-id";
import { StatusIndicator } from "@/components/shared/status-indicator";
import { StoreLink } from "@/components/shared/store-link";
import { Button } from "@/components/ui/button";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { useEscapeToClearFilters } from "@/hooks/use-escape-to-clear-filters";
import { downloadCsv } from "@/lib/csv";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";
import { getDefaultPageSize } from "@/lib/preferences";

const FILTER_DEFAULTS = {
  search: "",
  onlyActive: true,
  chain: "",
  store: "",
  page: 0,
  get pageSize() {
    return getDefaultPageSize();
  },
  sortId: "deviceId",
  sortDesc: false,
};

export function DevicesList({ storeName: embeddedStoreName }: { storeName?: string } = {}) {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { data: context, isLoading: contextLoading } = useBackofficeContext();
  // When embedded under a store's Devices tab (`storeName` set), the list is scoped to that store,
  // hides the Store/Chain columns and filters, and skips URL filter sync.
  const embedded = embeddedStoreName !== undefined;
  const routeSearch = useSearch({ strict: false }) as { f?: string };
  const f = embedded ? undefined : routeSearch.f;
  const initial = useMemo(() => decodeFilters(f, FILTER_DEFAULTS), [f]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initial.page,
    pageSize: initial.pageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([{ id: initial.sortId, desc: initial.sortDesc }]);
  const [search, setSearch] = useState(initial.search);
  const [onlyActive, setOnlyActive] = useState(initial.onlyActive);
  const [chainFilter, setChainFilter] = useState(initial.chain);
  const [storeFilter, setStoreFilter] = useState(initial.store);

  const sortBy = (sorting[0]?.id ?? "deviceId") as DevicesSortBy;
  const orderBy = sorting[0]?.desc ? ("desc" as const) : ("asc" as const);

  // Stores for the store filter dropdown — the full list (scoped to the selected chain, if any).
  // The dropdown is virtualized so it copes with the global store count.
  const { data: storesData, isLoading: storesLoading } = useStoresQuery({
    chainName: chainFilter || undefined,
    limit: -1,
    enabled: !embedded,
  });
  const storeOptions = useMemo(
    () =>
      [...(storesData?.stores ?? [])]
        .sort((a, b) => a.legibleName.localeCompare(b.legibleName))
        .map((s) => ({ label: s.legibleName, value: s.storeName })),
    [storesData?.stores],
  );

  const isFiltered = !!(search || chainFilter || storeFilter || !onlyActive);

  const clearFilters = useCallback(function clearFilters() {
    setSearch("");
    setChainFilter("");
    setStoreFilter("");
    setOnlyActive(true);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  useEscapeToClearFilters({ isFiltered, clearFilters });

  // The single description of "what is filtered right now" — shared by the URL sync and the presets.
  const currentFilters = useMemo(
    () => ({
      search,
      onlyActive,
      chain: chainFilter,
      store: storeFilter,
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      sortId: sorting[0]?.id ?? "deviceId",
      sortDesc: sorting[0]?.desc ?? false,
    }),
    [search, onlyActive, chainFilter, storeFilter, pagination.pageIndex, pagination.pageSize, sorting],
  );

  const syncUrl = useCallback(
    (overrides: Partial<typeof FILTER_DEFAULTS> = {}) => {
      // Embedded under a store tab: don't write filter state to the URL (the route isn't /hardware/devices).
      if (embedded) return;
      const encoded = encodeFilters({ ...currentFilters, ...overrides }, FILTER_DEFAULTS);
      navigate({
        to: "/hardware/devices",
        search: encoded ? { f: encoded } : {},
        replace: true,
      });
    },
    [embedded, currentFilters, navigate],
  );

  const applyPreset = useCallback(function applyPreset(next: typeof FILTER_DEFAULTS) {
    setSearch(next.search);
    setOnlyActive(next.onlyActive);
    setChainFilter(next.chain);
    setStoreFilter(next.store);
    setSorting([{ id: next.sortId, desc: next.sortDesc }]);
    // A preset carries no page index, and page size stays the viewer's own preference.
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  useEffect(
    function synchronizeFiltersToUrl() {
      syncUrl();
    },
    [syncUrl],
  );

  const { data, isLoading, refetch, isFetching } = useDevicesQuery({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: search || undefined,
    sortBy,
    orderBy,
    onlyActive: onlyActive || undefined,
    chainName: embedded ? undefined : chainFilter || undefined,
    storeName: embedded ? embeddedStoreName : storeFilter || undefined,
  });

  // Names live on the relevant chains/stores lists (relevant-entities pattern), not on each
  // device entry — resolve them by id.
  const chainMap = useMemo(() => new Map((data?.chains ?? []).map((c) => [c.chainId, c])), [data?.chains]);
  const storeMap = useMemo(() => new Map((data?.stores ?? []).map((s) => [s.storeId, s])), [data?.stores]);

  const handleExport = useCallback(async () => {
    const res = await api.GET("/v1/hardware/devices/", {
      params: {
        query: {
          page: 1,
          limit: -1,
          search: search || undefined,
          sortBy,
          orderBy,
          "filter[chainName]": embedded ? undefined : chainFilter || undefined,
          "filter[storeName]": embedded ? embeddedStoreName : storeFilter || undefined,
          "filter[onlyActive]": onlyActive || undefined,
        },
      },
    });
    const result = res.data as unknown as DevicesListResult;
    const stores = new Map(result.stores.map((s) => [s.storeId, s]));
    const chains = new Map(result.chains.map((c) => [c.chainId, c]));
    downloadCsv(
      "devices.csv",
      ["ID", "Name", "Store", "Chain", "Status", "Last Image", "Last Status"],
      result.devices.map((d) => [
        String(d.deviceId),
        d.deviceName,
        stores.get(d.storeId)?.storeDisplay ?? "",
        (d.chainId != null ? chains.get(d.chainId)?.legibleName : undefined) ?? "",
        d.isActive ? "Active" : "Inactive",
        d.lastImageAt ?? "",
        d.lastStatusAt ?? "",
      ]),
    );
  }, [embedded, embeddedStoreName, search, sortBy, orderBy, chainFilter, storeFilter, onlyActive]);

  const columns = useMemo<DataTableColumnDef<DeviceEntry>[]>(() => {
    const cols: DataTableColumnDef<DeviceEntry>[] = [
      {
        accessorKey: "deviceId",
        header: t("devices.list.columns.id"),
        size: 80,
        enableHiding: false,
        meta: { columnLabel: t("devices.list.columns.id"), className: "pl-4", forceWidth: true, sticky: "left" },
        cell: ({ row }) => <RowId id={row.original.deviceId} />,
      },
      {
        accessorKey: "deviceName",
        header: t("devices.list.columns.name"),
        meta: { columnLabel: t("devices.list.columns.name") },
        cell: ({ row }) => <span className="font-mono text-sm">{row.original.deviceName}</span>,
      },
      {
        accessorKey: "storeLegibleName",
        header: t("devices.list.columns.store"),
        meta: { columnLabel: t("devices.list.columns.store") },
        cell: ({ row }) => {
          const store = storeMap.get(row.original.storeId);
          return store ? <StoreLink storeName={store.storeName} legibleName={store.storeDisplay} /> : "—";
        },
      },
      {
        accessorKey: "chainLegibleName",
        header: t("devices.list.columns.chain"),
        enableSorting: false,
        meta: { columnLabel: t("devices.list.columns.chain"), defaultHidden: true },
        cell: ({ row }) => {
          const chain = row.original.chainId != null ? chainMap.get(row.original.chainId) : undefined;
          return chain ? <ChainLink chainName={chain.chainName} legibleName={chain.legibleName} /> : "—";
        },
      },
      {
        accessorKey: "lastImageAt",
        header: t("devices.list.columns.lastImage"),
        // Pin first-sort direction: with manualSorting, TanStack otherwise infers it from the first
        // row's value type, which flips to desc once asc brings NULL timestamps to the top — breaking
        // the none→asc→desc→none cycle (it would skip desc).
        sortDescFirst: false,
        meta: { columnLabel: t("devices.list.columns.lastImage") },
        cell: ({ row }) => <DateTimeValue value={row.original.lastImageAt} />,
      },
      {
        accessorKey: "lastStatusAt",
        header: t("devices.list.columns.lastStatus"),
        sortDescFirst: false,
        meta: { columnLabel: t("devices.list.columns.lastStatus") },
        cell: ({ row }) => <DateTimeValue value={row.original.lastStatusAt} />,
      },
      {
        accessorKey: "createdAt",
        header: t("devices.list.columns.createdAt"),
        meta: { columnLabel: t("devices.list.columns.createdAt"), defaultHidden: true },
        cell: ({ row }) => (row.original.createdAt ? <DateValue value={row.original.createdAt} /> : "—"),
      },
      {
        id: "isActive",
        header: t("devices.list.columns.status"),
        size: 80,
        enableSorting: false,
        meta: { columnLabel: t("devices.list.columns.status") },
        cell: ({ row }) => <StatusIndicator active={row.original.isActive} />,
      },
    ];
    // Embedded under a store tab → the store is fixed and its chain implied, so drop those columns.
    return embedded
      ? cols.filter(
          (col) =>
            !(
              "accessorKey" in col &&
              (col.accessorKey === "storeLegibleName" || col.accessorKey === "chainLegibleName")
            ),
        )
      : cols;
  }, [t, storeMap, chainMap, embedded]);

  return (
    <div>
      {!embedded && <PageHeader title={t("devices.title")} />}
      <div className="p-6">
        <DataTable
          storageKey={embedded ? "store-devices-list" : "devices-list"}
          columns={columns}
          data={data?.devices ?? []}
          totalCount={data?.count ?? 0}
          isLoading={isLoading}
          pagination={pagination}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={setSorting}
          search={search}
          onSearchChange={setSearch}
          searchDebounceMs={1000}
          onRefresh={refetch}
          isRefreshing={isFetching}
          onExport={handleExport}
          searchPlaceholder={t("devices.list.searchPlaceholder")}
          presets={
            embedded ? undefined : (
              <FilterPresets
                storageKey="devices-list"
                defaults={FILTER_DEFAULTS}
                current={currentFilters}
                onApply={applyPreset}
              />
            )
          }
          filters={
            <>
              {!embedded && (
                <>
                  <FilterSelect
                    label={t("devices.list.filter.chain")}
                    value={chainFilter}
                    onChange={(v) => {
                      setChainFilter(v);
                      setStoreFilter("");
                      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    }}
                    options={(context?.chains ?? []).map((c) => ({ label: c.display, value: c.value }))}
                    searchPlaceholder={t("stores.form.searchChains")}
                    emptyText={t("stores.form.noChains")}
                    loading={contextLoading}
                  />
                  <FilterSelect
                    label={t("devices.list.filter.store")}
                    value={storeFilter}
                    onChange={(v) => {
                      setStoreFilter(v);
                      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    }}
                    options={storeOptions}
                    searchPlaceholder={t("stores.operations.searchOption")}
                    emptyText={t("stores.operations.noOption")}
                    loading={storesLoading}
                  />
                </>
              )}
              <FilterToggle
                active={onlyActive}
                onChange={() => {
                  setOnlyActive((prev) => !prev);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                icon={Filter}
                label={t("common.active")}
              />
              {isFiltered && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-muted-foreground hover:text-foreground"
                  onClick={clearFilters}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </>
          }
        />
      </div>
    </div>
  );
}
